import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { BsBank2 } from "react-icons/bs";
import { FaCreditCard } from "react-icons/fa";
import { TbRepeat } from "react-icons/tb";
import { MdTimeline } from "react-icons/md";
import { IoAlertCircleOutline, IoCloseCircleOutline } from "react-icons/io5";
import { getCategoryIcon } from "../../utils/categoryIcons";
import { useNormalization } from "../../NormalizationContext";
import EditableAmount from "./EditableAmount";
import TransactionHistory from "./TransactionHistory";
import { transactionKey } from "../../utils/constants";

const COLORS = {
    darkText: "#333",
    secondaryText: "#666",
    tertiaryText: "#999",
    border: "#E0E0E0",
    cardBg: "#fff",
    increase: "#e74c3c",
    decrease: "#27ae60",
};

const sectionStyle = {
    marginBottom: "28px",
};

const headerStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    color: COLORS.darkText,
    marginBottom: "16px",
    direction: "rtl",
};

const getContentStyle = (isMobile) => ({
    direction: "rtl",
    background: COLORS.cardBg,
    borderRadius: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: isMobile ? "12px" : "16px 24px",
});

const getChartContainerStyle = (isMobile) => ({
    flexShrink: 0,
    width: isMobile ? "220px" : "280px",
    height: isMobile ? "220px" : "280px",
    position: "relative",
});

const centerLabelStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    pointerEvents: "none",
};

const getCenterAmountStyle = (isMobile) => ({
    fontSize: isMobile ? "14px" : "22px",
    fontWeight: "bold",
    color: COLORS.darkText,
});

const getCenterSubtitleStyle = (isMobile) => ({
    fontSize: isMobile ? "10px" : "12px",
    color: COLORS.tertiaryText,
});

const getLegendStyle = (isMobile) => ({
    direction: "rtl",
    overflow: "hidden",
});

const legendItemStyle = (isExpanded, isAllNormalized) => ({
    padding: "12px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    background: isAllNormalized ? "#f5f5f5" : isExpanded ? "#f8f9ff" : "transparent",
    borderBottom: `1px solid #f5f5f5`,
    transition: "all 0.2s ease",
    opacity: isAllNormalized ? 0.55 : 1,
});

const legendRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};

const legendLeftStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
    flex: 1,
};

const colorDotStyle = (color) => ({
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
});

const getCategoryNameStyle = (isMobile) => ({
    fontSize: isMobile ? "14px" : "16px",
    fontWeight: "600",
    color: COLORS.darkText,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const getLegendRightStyle = (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "4px" : "10px",
    flexShrink: 0,
});

const getAmountStyle = (isMobile) => ({
    fontSize: isMobile ? "14px" : "16px",
    fontWeight: "bold",
    color: COLORS.darkText,
    whiteSpace: "nowrap",
});

const percentageStyle = {
    fontSize: "13px",
    color: COLORS.tertiaryText,
    minWidth: "40px",
    textAlign: "left",
};

const diffBadgeStyle = (diff) => ({
    fontSize: "12px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "8px",
    background: diff > 0 ? "#ffeef0" : diff < 0 ? "#eefff5" : "#f5f5f5",
    color: diff > 0 ? COLORS.increase : diff < 0 ? COLORS.decrease : COLORS.tertiaryText,
});

const expandIconStyle = {
    color: COLORS.tertiaryText,
    flexShrink: 0,
};

const transactionListStyle = {
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: `1px dashed ${COLORS.border}`,
};

const getTransactionRowStyle = (isMobile, isAnomaly) => ({
    display: "grid",
    gridTemplateColumns: isMobile
        ? "1fr auto 24px 24px"
        : isAnomaly
            ? "1fr auto 30px 100px 120px 50px 30px"
            : "1fr 30px 100px 120px 30px 30px",
    alignItems: "center",
    padding: isMobile ? "6px 8px 6px 4px" : "8px 16px",
    fontSize: isMobile ? "12px" : "14px",
    color: COLORS.secondaryText,
    gap: isMobile ? "6px" : "10px",
    background: isAnomaly ? "#fff8f8" : "transparent",
    borderRadius: isAnomaly ? "6px" : "0",
    borderRight: isAnomaly ? "3px solid #ed578b" : "none",
    marginBottom: isAnomaly ? "2px" : "0",
});

const anomalyBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: "#e67e22",
    background: "#fff3e6",
    padding: "1px 6px",
    borderRadius: "4px",
    fontWeight: "500",
    whiteSpace: "nowrap",
};

const anomalySeparatorStyle = {
    borderTop: `1px dashed #f0c8c8`,
    margin: "6px 0",
    position: "relative",
};

const mobileActionBarStyle = {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    padding: "6px 8px",
    marginBottom: "4px",
    direction: "rtl",
};

const mobileActionBtnStyle = (isActive) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "6px",
    border: "none",
    fontSize: "11px",
    fontWeight: "600",
    cursor: "pointer",
    background: isActive ? "#e8f0ff" : "#f5f5f5",
    color: isActive ? "#4d79ff" : "#666",
    transition: "all 0.2s ease",
});

const mobileNormalizeBtnStyle = (isNormalized) => ({
    padding: "4px 10px",
    borderRadius: "6px",
    border: "none",
    fontSize: "11px",
    fontWeight: "bold",
    cursor: "pointer",
    background: isNormalized ? "#e8e8e8" : "#ff6b6b",
    color: isNormalized ? "#666" : "#fff",
    transition: "all 0.2s ease",
});

const normalizeTxIconBtnStyle = (isNormalized) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    display: "flex",
    alignItems: "center",
    color: isNormalized ? "#27ae60" : "#ed578b",
    transition: "color 0.2s ease",
});

const normalizeTxBtnStyle = (isNormalized) => ({
    padding: "2px 8px",
    borderRadius: "5px",
    border: "none",
    fontSize: "11px",
    fontWeight: "bold",
    cursor: "pointer",
    background: isNormalized ? "#e8e8e8" : "#ed578b",
    color: isNormalized ? COLORS.secondaryText : "#fff",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
});

const accountIconStyle = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: COLORS.tertiaryText,
    fontSize: "12px",
};

const showAllBtnStyle = {
    display: "block",
    margin: "12px auto 0",
    background: "none",
    border: "none",
    color: "#4d79ff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
};

const normalizeCategoryBtnStyle = (isNormalized) => ({
    padding: "3px 10px",
    borderRadius: "6px",
    border: "none",
    fontSize: "12px",
    fontWeight: "bold",
    cursor: "pointer",
    background: isNormalized ? "#e8e8e8" : "#ff6b6b",
    color: isNormalized ? COLORS.secondaryText : "#fff",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
});

const historyBtnStyle = (isActive) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    color: isActive ? "#4d79ff" : COLORS.tertiaryText,
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
    transition: "color 0.2s ease",
    opacity: isActive ? 1 : 0.7,
});

const recurringBtnStyle = (isRecurring) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    color: isRecurring ? "#27ae60" : COLORS.tertiaryText,
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
    transition: "color 0.2s ease",
    opacity: isRecurring ? 1 : 0.7,
});

const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 6}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
        </g>
    );
};

export default function CategoryBreakdown({ title, categories, transactions, type, onToggleRecurring, recurringDescriptions, isMobile = false }) {
    const { normalizedIds, normalizedAmounts, toggleNormalize, toggleNormalizeCategory, adjustAmount } = useNormalization();
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const total = categories.reduce((sum, c) => sum + c.amount, 0);
    const visibleCategories = showAll ? categories : categories.slice(0, 7);

    const toggleCategory = (categoryId) => {
        setExpandedCategoryId((prev) => (prev === categoryId ? null : categoryId));
    };

    const pieData = categories.map((c) => ({
        name: c.category,
        value: c.amount,
        color: c.color,
    }));

    return (
        <div style={sectionStyle}>
            <div style={headerStyle}>{title}</div>
            <div style={getContentStyle(isMobile)}>
                {/* <div style={getChartContainerStyle(isMobile)}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={isMobile ? 55 : 75}
                                outerRadius={isMobile ? 85 : 110}
                                dataKey="value"
                                onMouseEnter={(_, index) => setActiveIndex(index)}
                                onMouseLeave={() => setActiveIndex(null)}
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} stroke="none" />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={centerLabelStyle}>
                        <div style={getCenterAmountStyle(isMobile)}>{formatAmount(total)}₪</div>
                        <div style={getCenterSubtitleStyle(isMobile)}>סה״כ</div>
                    </div>
                </div> */}

                <div style={getLegendStyle(isMobile)}>
                    {visibleCategories.map((cat) => {
                        const isExpanded = expandedCategoryId === cat.categoryId;
                        const categoryTx = transactions[cat.categoryId] || [];
                        const allTxKeys = categoryTx.map((tx) => transactionKey(tx.type, tx.id));
                        const isAllNormalized = allTxKeys.length > 0 && allTxKeys.every((key) => normalizedIds.has(key));

                        const anomalyCount = categoryTx.filter((tx) => tx.isAnomaly).length;

                        return (
                            <div key={cat.categoryId} style={legendItemStyle(isExpanded, isAllNormalized)}>
                                <div
                                    style={legendRowStyle}
                                    onClick={() => toggleCategory(cat.categoryId)}
                                    onMouseEnter={() => {
                                        const idx = categories.findIndex((c) => c.categoryId === cat.categoryId);
                                        setActiveIndex(idx);
                                    }}
                                    onMouseLeave={() => setActiveIndex(null)}
                                >
                                    <div style={legendLeftStyle}>
                                        <div style={colorDotStyle(cat.color)} />
                                        <span style={{ fontSize: "16px", flexShrink: 0 }}>{getCategoryIcon(cat.category)}</span>
                                        <span style={getCategoryNameStyle(isMobile)}>{cat.category}</span>
                                        {anomalyCount > 0 && (
                                            <span style={anomalyBadgeStyle}>
                                                <IoAlertCircleOutline size={11} />
                                                {anomalyCount}
                                            </span>
                                        )}
                                    </div>
                                    <div style={getLegendRightStyle(isMobile)}>
                                        {cat.diffInPercentage !== 0 && (
                                            <span style={diffBadgeStyle(cat.diffInPercentage)}>
                                                {cat.diffInPercentage > 0 ? "+" : ""}{cat.diffInPercentage}%
                                            </span>
                                        )}
                                        <span style={getAmountStyle(isMobile)}>{formatAmount(cat.amount)}₪</span>
                                        {!isMobile && <span style={percentageStyle}>({cat.percentage}%)</span>}
                                        {!isMobile && (
                                            <button
                                                style={historyBtnStyle(selectedCategory?.categoryId === cat.categoryId)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedCategory(selectedCategory?.categoryId === cat.categoryId ? null : cat);
                                                }}
                                                title="היסטוריית קטגוריה"
                                            >
                                                <MdTimeline size={14} />
                                            </button>
                                        )}
                                        {!isMobile && categoryTx.length > 0 && (
                                            <button
                                                style={normalizeCategoryBtnStyle(isAllNormalized)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleNormalizeCategory(categoryTx, type, cat.categoryId);
                                                }}
                                                title={isAllNormalized ? "בטל נטרול קטגוריה" : "נטרל קטגוריה"}
                                            >
                                                {isAllNormalized ? "בטל נטרול" : "נטרל הכל"}
                                            </button>
                                        )}
                                        <span style={expandIconStyle}>
                                            {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                        </span>
                                    </div>
                                </div>

                                {isExpanded && categoryTx.length > 0 && (
                                    <div style={transactionListStyle}>
                                        {isMobile && (
                                            <div style={mobileActionBarStyle}>
                                                <button
                                                    style={mobileActionBtnStyle(selectedCategory?.categoryId === cat.categoryId)}
                                                    onClick={() => setSelectedCategory(selectedCategory?.categoryId === cat.categoryId ? null : cat)}
                                                >
                                                    <MdTimeline size={12} />
                                                    היסטוריה
                                                </button>
                                                <button
                                                    style={mobileNormalizeBtnStyle(isAllNormalized)}
                                                    onClick={() => toggleNormalizeCategory(categoryTx, type, cat.categoryId)}
                                                >
                                                    {isAllNormalized ? "בטל נטרול הכל" : "נטרל הכל"}
                                                </button>
                                            </div>
                                        )}
                                        {(() => {
                                            const anomalyTx = categoryTx.filter((tx) => tx.isAnomaly);
                                            const regularTx = categoryTx.filter((tx) => !tx.isAnomaly);
                                            const hasAnomalies = anomalyTx.length > 0;
                                            const allTx = [...anomalyTx, ...regularTx];

                                            return allTx.map((tx, idx) => {
                                                const txKey = transactionKey(tx.type, tx.id);
                                                const isRecurring = recurringDescriptions && recurringDescriptions.has((tx.description || "").toLowerCase());
                                                const isTxNormalized = normalizedIds.has(txKey);
                                                const showSeparator = hasAnomalies && idx === anomalyTx.length && regularTx.length > 0;

                                                return (
                                                    <React.Fragment key={txKey}>
                                                        {showSeparator && <div style={anomalySeparatorStyle} />}
                                                        <div style={getTransactionRowStyle(isMobile, tx.isAnomaly)}>
                                                            <span style={{ fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "6px" }}>
                                                                {tx.isAnomaly && <IoAlertCircleOutline size={14} color="#e67e22" style={{ flexShrink: 0 }} />}
                                                                {tx.description}
                                                            </span>
                                                            {tx.isAnomaly && !isMobile && (
                                                                <span style={anomalyBadgeStyle}>{tx.anomalyReason}</span>
                                                            )}
                                                            {!isMobile && (
                                                                <div style={{ ...accountIconStyle, justifyContent: "center" }}>
                                                                    {tx.accountType === "bank" ? <BsBank2 size={12} /> : <FaCreditCard size={12} />}
                                                                </div>
                                                            )}
                                                            {!isMobile && <span style={{ textAlign: "center" }}>{tx.date}</span>}
                                                            <span style={{ textAlign: "left" }}>
                                                                <EditableAmount
                                                                    originalAmount={tx.amount}
                                                                    adjustedAmount={normalizedAmounts && normalizedAmounts[txKey] ? tx.amount - normalizedAmounts[txKey].amount : undefined}
                                                                    onConfirm={(newAmount) => adjustAmount(txKey, tx.amount, newAmount, type, cat.categoryId)}
                                                                    transactionType={type}
                                                                />
                                                            </span>
                                                            {tx.isAnomaly ? (
                                                                isMobile ? (
                                                                    <button
                                                                        style={normalizeTxIconBtnStyle(isTxNormalized)}
                                                                        onClick={() => toggleNormalize(txKey, tx.amount, type, cat.categoryId)}
                                                                        title={isTxNormalized ? "בטל נטרול" : "נטרל"}
                                                                    >
                                                                        <IoCloseCircleOutline size={18} />
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        style={normalizeTxBtnStyle(isTxNormalized)}
                                                                        onClick={() => toggleNormalize(txKey, tx.amount, type, cat.categoryId)}
                                                                        title={isTxNormalized ? "בטל נטרול" : "נטרל"}
                                                                    >
                                                                        {isTxNormalized ? "בטל" : "נטרל"}
                                                                    </button>
                                                                )
                                                            ) : (
                                                                <button
                                                                    style={historyBtnStyle(selectedTransaction && transactionKey(selectedTransaction.type, selectedTransaction.id) === txKey)}
                                                                    onClick={() => setSelectedTransaction(selectedTransaction && transactionKey(selectedTransaction.type, selectedTransaction.id) === txKey ? null : tx)}
                                                                    title="היסטוריית סכומים"
                                                                >
                                                                    <MdTimeline size={14} />
                                                                </button>
                                                            )}
                                                            <button
                                                                style={recurringBtnStyle(isRecurring)}
                                                                onClick={() => onToggleRecurring && onToggleRecurring(tx)}
                                                                title={isRecurring ? "בטל סימון פעולה חוזרת" : "סמן כפעולה חוזרת"}
                                                            >
                                                                <TbRepeat size={14} />
                                                            </button>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            });
                                        })()}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {categories.length > 7 && (
                        <button style={showAllBtnStyle} onClick={() => setShowAll(!showAll)}>
                            {showAll ? "הצג פחות" : `הצג את כל ${categories.length} הקטגוריות`}
                        </button>
                    )}
                </div>
            </div>

            {selectedTransaction && (
                <TransactionHistory
                    description={selectedTransaction.description}
                    currentAmount={Math.abs(selectedTransaction.amount)}
                    onClose={() => setSelectedTransaction(null)}
                />
            )}

            {selectedCategory && (
                <TransactionHistory
                    description={selectedCategory.category}
                    categoryId={selectedCategory.categoryId}
                    currentAmount={selectedCategory.amount}
                    onClose={() => setSelectedCategory(null)}
                />
            )}
        </div>
    );
}

function formatAmount(amount) {
    return Number(amount).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
